/**
 * Phone Message Centre — Sprint 6 · Phase 1.
 *
 * "Capture every inbound communication, assign it to the right person,
 *  track it until completion, and preserve it as part of the operational
 *  record."  — PO 2026-02-04.
 *
 * V1 surfaces:
 *   • My Inbox — Urgent/Today/Tomorrow/FYI counts + completed-today.
 *   • Inbox list — with quick assign / priority / close actions.
 *   • New Message — with real customer autocomplete + non-customer fallback.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, Plus, X, MagnifyingGlass, CheckCircle, ArrowsClockwise,
  User, Buildings, Truck, Receipt, ListChecks, LinkSimple,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ActionsMenu from "@/components/ActionsMenu";

const pmApi = {
  users:     ()             => api.get("/phone-messages/users"),
  search:    (q)            => api.get("/phone-messages/customers/search", { params: { q, limit: 8 } }),
  list:      (params)       => api.get("/phone-messages", { params }),
  inbox:     (uid)          => api.get(`/phone-messages/inbox/${uid}/summary`),
  create:    (payload)      => api.post("/phone-messages", payload),
  patch:     (id, payload)  => api.patch(`/phone-messages/${id}`, payload),
  complete:  (id)           => api.post(`/phone-messages/${id}/complete`),
};

const REASONS = [
  ["fuel_order", "Fuel Order"], ["tank_requirements", "Tank Requirements"],
  ["pricing", "Pricing"], ["order_change", "Order Change"],
  ["delivery_status", "Delivery Status"], ["billing", "Billing"],
  ["ar", "Accounts Receivable"], ["ap", "Accounts Payable"],
  ["general", "General Message"], ["other", "Other"],
];
const PRIORITIES = [
  ["urgent",   "Urgent",   "bg-rose-100 text-rose-800 border-rose-300"],
  ["today",    "Today",    "bg-amber-100 text-amber-900 border-amber-300"],
  ["tomorrow", "Tomorrow", "bg-sky-100 text-sky-800 border-sky-300"],
  ["fyi",      "FYI",      "bg-slate-100 text-slate-700 border-slate-300"],
];
const PRIORITY_STYLE = Object.fromEntries(PRIORITIES.map(([k, l, c]) => [k, { label: l, chip: c }]));

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/* ------------------------------------------------------------------
   New Message Dialog
   ------------------------------------------------------------------ */
function NewMessageDialog({ open, onClose, users, onDone, prefillCustomer }) {
  const [assignedTo, setAssignedTo] = useState("");
  const [reason, setReason] = useState("fuel_order");
  const [priority, setPriority] = useState("today");
  const [source, setSource] = useState("phone");
  const [message, setMessage] = useState("");
  const [customerQ, setCustomerQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [callerName, setCallerName] = useState("");
  const [callerPhone, setCallerPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAssignedTo(users?.[0]?.id || "");
    // Sprint 6.1 · prefill customer when opened from Customer 360.
    if (prefillCustomer && prefillCustomer.id) {
      setSelected(prefillCustomer);
      setCustomerQ(`${prefillCustomer.account_number} · ${prefillCustomer.customer_name}`);
      setCallerName(prefillCustomer.primary_contact || prefillCustomer.customer_name || "");
      setCallerPhone(prefillCustomer.phone_number || "");
    }
  }, [open, users, prefillCustomer]);

  useEffect(() => {
    if (!customerQ || customerQ.length < 2) { setResults([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await pmApi.search(customerQ);
        if (alive) setResults(data.items || []);
      } catch { /* silent */ }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [customerQ]);

  const pickCustomer = (c) => {
    setSelected(c);
    setCustomerQ(`${c.account_number} · ${c.customer_name}`);
    setResults([]);
    // Fall back to customer_name when primary_contact is null (real data
    // often has null contacts). Operator can overwrite either field.
    setCallerName(c.primary_contact || c.customer_name || "");
    setCallerPhone(c.phone_number || "");
  };
  const clearCustomer = () => {
    setSelected(null); setCustomerQ(""); setResults([]);
    setCallerName(""); setCallerPhone("");
  };

  const submit = async () => {
    if (!assignedTo) { toast.error("Assign the message to someone"); return; }
    if (!message.trim()) { toast.error("Please add a message"); return; }
    if (!selected && !callerName && !callerPhone) {
      toast.error("Select a customer OR enter caller name/phone"); return;
    }
    setSaving(true);
    try {
      await pmApi.create({
        assigned_to:  assignedTo,
        reason,
        priority,
        source,
        message:      message.trim(),
        customer_id:  selected?.id || null,
        caller_name:  callerName || null,
        caller_phone: callerPhone || null,
      });
      toast.success("Message captured");
      // Reset & close.
      setMessage(""); clearCustomer(); onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
         data-testid="pm-new-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-amber-600" weight="fill" />
            <h3 className="text-base font-semibold text-slate-900">New Phone Message</h3>
          </div>
          <button onClick={onClose} data-testid="pm-new-close" className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Assign to</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}
                      data-testid="pm-new-assigned"
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5">
                {(users || []).map((u) => (
                  <option key={u.id} value={u.id}>{`${u.full_name} · ${u.role}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Source</label>
              <select value={source} onChange={(e) => setSource(e.target.value)}
                      data-testid="pm-new-source"
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5">
                <option value="phone">Phone</option>
                <option value="walk_in">Walk-in</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600">Customer (search by account #, company, alpha code, contact)</label>
            <div className="mt-1 relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-2.5 text-slate-400" />
              <input value={customerQ}
                     onChange={(e) => { setCustomerQ(e.target.value); if (selected) setSelected(null); }}
                     data-testid="pm-new-customer-search"
                     placeholder="e.g. Jones"
                     className="w-full text-sm rounded border border-slate-300 pl-7 pr-8 py-1.5" />
              {selected ? (
                <button onClick={clearCustomer} data-testid="pm-new-customer-clear"
                        className="absolute right-2 top-2 text-slate-400 hover:text-rose-600"><X size={14}/></button>
              ) : null}
              {results.length > 0 && !selected ? (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded shadow max-h-56 overflow-y-auto"
                     data-testid="pm-new-customer-results">
                  {results.map((c) => (
                    <button key={c.id} onClick={() => pickCustomer(c)}
                            data-testid={`pm-new-customer-hit-${c.id}`}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                      <div className="font-medium text-slate-800">{c.account_number} · {c.customer_name}</div>
                      <div className="text-[11px] text-slate-500">{c.primary_contact || "—"} · {c.phone_number || "—"}</div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">No match? Leave blank and use the caller fields below (works for vendors, prospects, drivers).</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Caller name</label>
              <input value={callerName} onChange={(e) => setCallerName(e.target.value)}
                     data-testid="pm-new-caller-name"
                     className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Caller phone</label>
              <input value={callerPhone} onChange={(e) => setCallerPhone(e.target.value)}
                     data-testid="pm-new-caller-phone"
                     className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Reason</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="pm-new-reason"
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5">
                {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}
                      data-testid="pm-new-priority"
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5">
                {PRIORITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600">Message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                      data-testid="pm-new-message"
                      rows={5}
                      placeholder="What did the caller say? Any callback timing, order refs, next steps?"
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} data-testid="pm-new-cancel"
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="pm-new-submit"
                  className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            {saving ? "Saving…" : "Save Message"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------
   Message Row
   ------------------------------------------------------------------ */
function MessageRow({ m, users, canWrite, onChanged }) {
  const p = PRIORITY_STYLE[m.priority] || PRIORITY_STYLE.fyi;
  // DEC-052 · linked-task count · round-trip visibility.
  const [linkedCount, setLinkedCount] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/tasks?source_type=phone_message&source_id=${encodeURIComponent(m.id)}`)
       .then((r) => { if (alive) setLinkedCount((r.data || []).length); })
       .catch(() => { if (alive) setLinkedCount(0); });
    return () => { alive = false; };
  }, [m.id]);

  const reassign = async (newUser) => {
    try { await pmApi.patch(m.id, { assigned_to: newUser }); onChanged(); }
    catch { toast.error("Reassign failed"); }
  };
  const changePriority = async (newPri) => {
    try { await pmApi.patch(m.id, { priority: newPri }); onChanged(); }
    catch { toast.error("Update failed"); }
  };
  const close = async () => {
    try { await pmApi.complete(m.id); toast.success("Message closed"); onChanged(); }
    catch { toast.error("Close failed"); }
  };

  // Reusable action prefill · every action carries provenance back to this
  // phone message so the linked-task badge stays accurate.
  const _basePrefill = {
    customer_account: m.customer?.account_number,
    customer_name:    m.customer?.customer_name,
    source_type:      "phone_message",
    source_id:        m.id,
    source_ref:       {
      section:      "root",
      caller_name:  m.caller_name || null,
      caller_phone: m.customer?.phone_number || m.caller_phone || null,
    },
  };
  const _subjectFor = (verb) => {
    const who = m.customer?.customer_name || m.caller_name || m.caller_phone || "caller";
    return `${verb} · ${who}`;
  };
  const actions = [
    { key: "create-fuel-order",  label: "Create Fuel Order", icon: Truck,
      prefill: { ..._basePrefill, task_type: "Dispatch",
                 subject: _subjectFor("Fuel order"),
                 description: m.message, priority: m.priority === "urgent" ? "Urgent" : "Normal" } },
    { key: "create-ar-followup", label: "Create A/R Follow-up", icon: Receipt,
      prefill: { ..._basePrefill, task_type: "Accounts Receivable",
                 subject: _subjectFor("A/R follow-up"), description: m.message } },
    { key: "create-general",     label: "Create General Task", icon: ListChecks,
      prefill: { ..._basePrefill, task_type: "General",
                 subject: _subjectFor("Follow up"), description: m.message } },
  ];

  const onActionCreated = () => {
    setLinkedCount((c) => (c ?? 0) + 1);
    toast.success("Task created and linked to this message");
    onChanged?.();
  };

  return (
    <li className="border-t border-slate-100 p-3 hover:bg-slate-50"
        data-testid={`pm-row-${m.id}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${p.chip}`}
              data-testid={`pm-priority-${m.id}`}>
          {p.label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">
              {m.customer?.customer_name || m.caller_name || m.caller_phone || "Unknown caller"}
            </span>
            {m.customer?.account_number ? (
              <span className="text-[11px] text-slate-500">#{m.customer.account_number}</span>
            ) : null}
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600">
              {REASONS.find(([k]) => k === m.reason)?.[1] || m.reason}
            </span>
            <span className="text-[10px] text-slate-500">{m.source === "walk_in" ? "Walk-in" : "Phone"}</span>
            {linkedCount ? (
              <span data-testid={`pm-linked-tasks-${m.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300">
                <LinkSimple size={9} weight="bold" />
                {linkedCount} linked task{linkedCount === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{m.message}</p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500 flex-wrap">
            <span>Assigned to <b className="text-slate-700">{m.assigned_to_name || "—"}</b></span>
            <span>· By {m.created_by_name || "—"}</span>
            <span>· {fmtDT(m.created_at)}</span>
            {m.customer?.phone_number || m.caller_phone ? (
              <span>· <b>{m.customer?.phone_number || m.caller_phone}</b></span>
            ) : null}
          </div>
        </div>
        {canWrite && m.status !== "closed" ? (
          <div className="flex items-center gap-1 shrink-0">
            <select value={m.priority} onChange={(e) => changePriority(e.target.value)}
                    data-testid={`pm-set-priority-${m.id}`}
                    className="text-[11px] rounded border border-slate-300 px-1 py-0.5">
              {PRIORITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select value={m.assigned_to} onChange={(e) => reassign(e.target.value)}
                    data-testid={`pm-reassign-${m.id}`}
                    className="text-[11px] rounded border border-slate-300 px-1 py-0.5 max-w-[140px]">
              {(users || []).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
            {/* DEC-052 · Workflow Actions */}
            <ActionsMenu
              actions={actions}
              onCreated={onActionCreated}
              dataTestidPrefix={`pm-actions-${m.id}`}
              buttonLabel="Actions"
            />
            <button onClick={close} data-testid={`pm-close-${m.id}`}
                    className="text-[11px] px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
              <CheckCircle size={12} /> Close
            </button>
          </div>
        ) : m.status === "closed" ? (
          <span className="text-[11px] text-emerald-700 shrink-0">Closed · {m.completed_by_name}</span>
        ) : null}
      </div>
    </li>
  );
}


/* ------------------------------------------------------------------
   Root
   ------------------------------------------------------------------ */
export default function PhoneMessages() {
  const { user } = useAuth();
  const canWrite = user?.role === "Administrator" || user?.role === "Owner" || user?.role === "Office Staff";
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCustomerId = searchParams.get("customer_id");
  const urlNew = searchParams.get("new") === "1";
  const urlOpen = searchParams.get("open") === "1";
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);   // user record for the current auth email
  const [inbox, setInbox] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState(urlCustomerId ? "customer" : urlOpen ? "all_open" : "mine_open");
  const [dialog, setDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Sprint 6.1 · when arriving with ?customer_id, load that customer's
  // identity so we can label the pinned filter chip *and* prefill the
  // New Message dialog. Uses the existing customers endpoint.
  const [pinnedCustomer, setPinnedCustomer] = useState(null);

  useEffect(() => {
    if (!urlCustomerId) { setPinnedCustomer(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/customers/${urlCustomerId}`);
        if (alive) setPinnedCustomer(data);
      } catch { /* silent · pinned chip just shows the id */ }
    })();
    return () => { alive = false; };
  }, [urlCustomerId]);

  // Sprint 6.1 · open the New Message dialog when the caller drilled in
  // from Customer 360 with ?new=1. Consume the flag from the URL so a
  // page refresh doesn't re-open the dialog.
  useEffect(() => {
    if (urlNew && canWrite) {
      setDialog(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlNew, canWrite]);

  const clearCustomerFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer_id");
    setSearchParams(next, { replace: false });
    setFilter("mine_open");
  };

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await pmApi.users();
      setUsers(data.items || []);
      const mine = (data.items || []).find((u) => u.email?.toLowerCase() === (user?.email || "").toLowerCase());
      setMe(mine || null);
    } catch { /* silent */ }
  }, [user?.email]);

  const loadInbox = useCallback(async () => {
    if (!me?.id) return;
    try { const { data } = await pmApi.inbox(me.id); setInbox(data); }
    catch { /* silent */ }
  }, [me?.id]);

  const loadMessages = useCallback(async () => {
    // Customer view · Sprint 6.1 · show ALL messages for that customer
    // regardless of assignee/status. Works without ``me`` being loaded.
    if (urlCustomerId) {
      try {
        const { data } = await pmApi.list({ customer_id: urlCustomerId, limit: 200 });
        setMessages(data.items || []);
      } catch { /* silent */ }
      return;
    }
    if (!me?.id) return;
    let params = {};
    if (filter === "mine_open")   params = { assigned_to: me.id };
    if (filter === "all_open")    params = { };
    if (filter === "completed")   params = { completed_today: true };
    try {
      const { data } = await pmApi.list(params);
      const rows = (data.items || []).filter((m) => {
        if (filter === "mine_open" || filter === "all_open") return m.status !== "closed";
        return true;
      });
      setMessages(rows);
    } catch { /* silent */ }
  }, [me?.id, filter, urlCustomerId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadInbox();  loadMessages(); }, [loadInbox, loadMessages, refreshKey]);

  const bump = () => setRefreshKey((k) => k + 1);

  const priorityCards = useMemo(() => {
    const c = inbox?.counts || { urgent: 0, today: 0, tomorrow: 0, fyi: 0 };
    return PRIORITIES.map(([k, label, chip]) => ({ key: k, label, chip, count: c[k] || 0 }));
  }, [inbox]);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="pm-workspace">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Phone Message Centre</div>
          <h1 className="text-2xl font-semibold text-slate-900">My Messages</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Capture every inbound call, assign it, and close it out. Every message is preserved as part of the operational record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={bump} data-testid="pm-refresh"
                  className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"><ArrowsClockwise size={12}/> Refresh</button>
          {canWrite ? (
            <button onClick={() => setDialog(true)} data-testid="pm-new"
                    className="text-sm font-semibold px-4 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1.5 shadow-sm">
              <Plus size={14} weight="bold"/> New Phone Message
            </button>
          ) : null}
        </div>
      </header>

      {/* Sprint 6.1 · pinned customer filter when drilled in from Customer 360 */}
      {urlCustomerId ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
             data-testid="pm-customer-filter-chip">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <Buildings size={14} weight="bold" />
            <span>
              Showing messages for{" "}
              <b data-testid="pm-customer-filter-name">
                {pinnedCustomer
                  ? `${pinnedCustomer.account_number} · ${pinnedCustomer.customer_name}`
                  : urlCustomerId}
              </b>
            </span>
          </div>
          <button onClick={clearCustomerFilter} data-testid="pm-customer-filter-clear"
                  className="text-xs font-medium text-amber-900 hover:text-amber-700 inline-flex items-center gap-1">
            <X size={12} /> Clear filter
          </button>
        </div>
      ) : null}

      {/* Priority summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pm-summary">
        {priorityCards.map((p) => (
          <div key={p.key} data-testid={`pm-count-${p.key}`}
               className={`rounded-md border px-3 py-3 ${p.chip}`}>
            <div className="text-[10px] uppercase tracking-wide font-semibold">{p.label}</div>
            <div className="text-2xl font-semibold tabular-nums">{p.count}</div>
          </div>
        ))}
        <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-3"
             data-testid="pm-count-completed">
          <div className="text-[10px] uppercase tracking-wide font-semibold">Completed Today</div>
          <div className="text-2xl font-semibold tabular-nums">{inbox?.completed_today ?? 0}</div>
        </div>
      </div>

      {/* Filter tabs · hidden when pinned to a single customer */}
      {!urlCustomerId ? (
        <div className="flex items-center gap-1 border-b border-slate-200" data-testid="pm-filters">
          {[
            ["mine_open", "My Inbox"],
            ["all_open",  "All Open"],
            ["completed", "Completed Today"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`pm-filter-${k}`}
                    className={`px-3 py-2 text-sm border-b-2 ${filter === k ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              {l}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-500" data-testid="pm-count-listed">
            {messages.length} message{messages.length === 1 ? "" : "s"}
          </span>
        </div>
      ) : (
        <div className="flex items-center border-b border-slate-200 pb-2" data-testid="pm-filters-customer">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Customer history
          </span>
          <span className="ml-auto text-xs text-slate-500" data-testid="pm-count-listed">
            {messages.length} message{messages.length === 1 ? "" : "s"}
          </span>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {messages.length === 0 ? (
          <div className="p-10 text-center" data-testid="pm-empty">
            <Phone size={28} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-700 font-medium">No open phone messages.</p>
            <p className="text-xs text-slate-500 mt-1">
              {filter === "mine_open" ? "Your inbox is clear." : filter === "all_open" ? "Nothing awaiting attention." : "No messages match."}
            </p>
            {canWrite ? (
              <button onClick={() => setDialog(true)} data-testid="pm-empty-new"
                      className="mt-4 text-sm font-semibold px-4 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1.5 shadow-sm">
                <Plus size={14} weight="bold"/> New Phone Message
              </button>
            ) : null}
          </div>
        ) : (
          <ul>
            {messages.map((m) => (
              <MessageRow key={m.id} m={m} users={users} canWrite={canWrite} onChanged={bump} />
            ))}
          </ul>
        )}
      </div>

      <NewMessageDialog open={dialog} onClose={() => setDialog(false)}
                        users={users} onDone={bump}
                        prefillCustomer={pinnedCustomer} />
    </div>
  );
}
